import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GoogleAuthService } from './google-auth.service';

// Exported interfaces
export interface ApiSubject {
  id: string;
  name_eng: string;
  name_hindi: string;
}

export interface SubjectSection {
  subjects: ApiSubject[];
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class InternalApiService {
  private baseUrl = environment.apiBaseUrl;
  private internalApiUrl = `${this.baseUrl}/api/internal`;

  constructor(
    private http: HttpClient,
    private googleAuthService: GoogleAuthService
  ) { }

  /**
   * Generic GET method with authentication
   */
  get<T>(endpoint: string): Observable<T> {
    const headers = this.googleAuthService.getAuthHeaders();
    return this.http.get<T>(`${this.baseUrl}${endpoint}`, { headers });
  }

  /**
   * Generic POST method with authentication
   */
  post<T>(endpoint: string, data: any): Observable<T> {
    const headers = this.googleAuthService.getAuthHeaders();
    return this.http.post<T>(`${this.baseUrl}${endpoint}`, data, { headers });
  }

  /**
   * Generic PUT method with authentication
   */
  put<T>(endpoint: string, data: any): Observable<T> {
    const headers = this.googleAuthService.getAuthHeaders();
    return this.http.put<T>(`${this.baseUrl}${endpoint}`, data, { headers });
  }

  /**
   * Generic DELETE method with authentication
   */
  delete<T>(endpoint: string): Observable<T> {
    const headers = this.googleAuthService.getAuthHeaders();
    return this.http.delete<T>(`${this.baseUrl}${endpoint}`, { headers });
  }

  /**
   * Get all subjects and sections for the internal upload form
   */
  getSubjectsSections(): Observable<SubjectSection> {
    const headers = this.googleAuthService.getAuthHeaders();
    return this.http.get<SubjectSection>(`${this.internalApiUrl}/subjects-sections`, { headers });
  }

  /**
   * Get sections for a specific subject
   */
  getSectionsForSubject(subject: string): Observable<string[]> {
    const headers = this.googleAuthService.getAuthHeaders();
    return this.http.get<string[]>(`${this.internalApiUrl}/subjects/${subject}/sections`, { headers });
  }

  /**
   * Helper method to handle errors
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      return new Observable<T>(subscriber => {
        subscriber.next(result as T);
        subscriber.complete();
      });
    };
  }
} 